/**
 * Prevent WebP Conversion from SEO Apps
 * 
 * This script prevents SEO apps (SEOAnt, AVADA SEO, etc.) from dynamically
 * converting images to WebP format. Native WebP files are allowed and preserved.
 * 
 * What it does:
 * - ✅ Allows native .webp files (uploaded as WebP)
 * - ❌ Blocks SEO app dynamic conversions (format=webp parameters)
 * - ❌ Blocks SEO app CDN proxies
 * - ✅ Preserves Cloudflare image delivery
 * - ✅ Monitors and restores modified images
 * 
 * What it prevents:
 * - SEO apps adding ?format=webp to URLs
 * - SEO apps routing images through their CDN
 * - Dynamic on-the-fly WebP conversion
 */

(function() {
  'use strict';

  const config = {
    cloudflareAccount: 'kqtAfraFTcgD333DFm79cw',
    cloudflareDeliveryUrl: 'https://imagedelivery.net/kqtAfraFTcgD333DFm79cw',
    preventWebP: true,
    debug: false
  };

  // Log helper
  function log(...args) {
    if (config.debug) {
      console.log('[Prevent WebP]', ...args);
    }
  }

  /**
   * Check if an image URL is a WebP conversion from SEO apps
   * Note: We allow native WebP files, but block SEO app conversions
   */
  function isWebPConversion(url) {
    if (!url) return false;
    
    // Allow Cloudflare images (they can be WebP)
    if (url.includes('imagedelivery.net')) {
      return false;
    }
    
    // Check for SEO app CDN conversions (these are the problem)
    const seoAppCDNs = [
      'seoant-cdn',
      'avada-seo',
      'optimize-seo'
    ];
    
    if (seoAppCDNs.some(cdn => url.includes(cdn))) {
      return true;
    }
    
    // Check for dynamic WebP conversion parameters (SEO apps add these)
    // format=webp or f=webp parameters indicate conversion, not native WebP
    if (url.includes('format=webp') || url.includes('f=webp') || url.includes('fm=webp')) {
      return true;
    }
    
    // Native WebP files from Shopify CDN are OK
    // Only flag if it's a suspicious conversion pattern
    return false;
  }

  /**
   * Get original Shopify image URL from a potentially modified URL
   * Only removes SEO app conversion parameters, keeps native WebP files
   */
  function getOriginalImageUrl(url) {
    if (!url) return url;
    
    // If it's already a Cloudflare image, keep it
    if (url.includes('imagedelivery.net')) {
      return url;
    }
    
    // Remove SEO app conversion parameters (but keep native .webp files)
    url = url.replace(/[?&]format=webp/gi, '');
    url = url.replace(/[?&]f=webp/gi, '');
    url = url.replace(/[?&]fm=webp/gi, '');
    
    // Remove SEO app CDN prefixes
    const seoAppCDNs = [
      'seoant-cdn',
      'avada-seo',
      'optimize-seo'
    ];
    
    seoAppCDNs.forEach(cdn => {
      const pattern = new RegExp(`https?://[^/]*${cdn}[^/]*/`, 'gi');
      url = url.replace(pattern, 'https://cdn.shopify.com/');
    });
    
    // Ensure it's using Shopify CDN or Cloudflare
    if (!url.includes('cdn.shopify.com') && !url.includes('imagedelivery.net')) {
      log('Warning: Unexpected image URL format:', url);
    }
    
    return url;
  }

  /**
   * Restore image to original format
   */
  function restoreImage(img) {
    const originalSrc = img.getAttribute('data-original-src') || img.src;
    const currentSrc = img.src;
    
    if (isWebPConversion(currentSrc)) {
      const restoredSrc = getOriginalImageUrl(originalSrc);
      
      if (restoredSrc !== currentSrc) {
        log('Restoring image from WebP:', currentSrc, '->', restoredSrc);
        
        // Store original if not already stored
        if (!img.getAttribute('data-original-src')) {
          img.setAttribute('data-original-src', originalSrc);
        }
        
        // Restore the image
        img.src = restoredSrc;
        
        // Also fix srcset if present
        if (img.srcset) {
          const srcsetParts = img.srcset.split(',').map(part => {
            const [url, descriptor] = part.trim().split(/\s+/);
            const restoredUrl = getOriginalImageUrl(url);
            return descriptor ? `${restoredUrl} ${descriptor}` : restoredUrl;
          });
          img.srcset = srcsetParts.join(', ');
        }
        
        img.setAttribute('data-webp-prevented', 'true');
      }
    }
  }

  /**
   * Monitor all images on the page
   */
  function monitorImages() {
    const images = document.querySelectorAll('img');
    
    images.forEach(img => {
      // Skip if already processed
      if (img.getAttribute('data-webp-prevented')) {
        return;
      }
      
      // Check and restore if needed
      restoreImage(img);
      
      // Monitor for changes
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'src' || mutation.attributeName === 'srcset')) {
            restoreImage(img);
          }
        });
      });
      
      observer.observe(img, {
        attributes: true,
        attributeFilter: ['src', 'srcset']
      });
    });
    
    log(`Monitored ${images.length} images`);
  }

  /**
   * Intercept image loading
   */
  function interceptImageLoading() {
    // Override Image constructor
    const OriginalImage = window.Image;
    window.Image = function() {
      const img = new OriginalImage();
      
      // Intercept src setter
      const originalSrcDescriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
      Object.defineProperty(img, 'src', {
        get: function() {
          return originalSrcDescriptor.get.call(this);
        },
        set: function(value) {
          const processedValue = config.preventWebP ? getOriginalImageUrl(value) : value;
          if (processedValue !== value) {
            log('Intercepted Image.src:', value, '->', processedValue);
          }
          originalSrcDescriptor.set.call(this, processedValue);
        }
      });
      
      return img;
    };
    
    log('Image loading interception enabled');
  }

  /**
   * Block WebP conversion scripts
   */
  function blockWebPScripts() {
    // Monitor script additions
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.tagName === 'SCRIPT' && node.src) {
            // Check if it's a WebP conversion script
            const suspiciousPatterns = [
              'webp-converter',
              'image-optimizer',
              'hyperspeed.*image',
              'seoant.*image'
            ];
            
            const isSuspicious = suspiciousPatterns.some(pattern => 
              new RegExp(pattern, 'i').test(node.src)
            );
            
            if (isSuspicious) {
              log('Blocked suspicious image script:', node.src);
              node.remove();
            }
          }
        });
      });
    });
    
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Add Cloudflare image support indicator
   */
  function addCloudflareIndicator() {
    window.CloudflareImagesEnabled = true;
    window.CloudflareConfig = {
      accountHash: config.cloudflareAccount,
      deliveryUrl: config.cloudflareDeliveryUrl
    };
    
    log('Cloudflare Images support enabled');
  }

  /**
   * Initialize
   */
  function init() {
    log('Initializing WebP prevention...');
    
    // Add Cloudflare support
    addCloudflareIndicator();
    
    // Block WebP scripts
    if (config.preventWebP) {
      blockWebPScripts();
      interceptImageLoading();
    }
    
    // Monitor existing images
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', monitorImages);
    } else {
      monitorImages();
    }
    
    // Monitor for dynamically added images
    const observer = new MutationObserver(mutations => {
      let hasNewImages = false;
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.tagName === 'IMG' || 
              (node.querySelectorAll && node.querySelectorAll('img').length > 0)) {
            hasNewImages = true;
          }
        });
      });
      
      if (hasNewImages) {
        setTimeout(monitorImages, 100);
      }
    });
    
    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true
    });
    
    log('WebP prevention initialized');
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API for debugging
  window.PreventWebP = {
    config,
    restoreImage,
    monitorImages,
    isWebPConversion,
    getOriginalImageUrl
  };

})();
